import { useMemo, useState, useEffect } from 'react';

/**
 * usePaginated — page through a client-side array.
 *
 *   const { page, pageSize, setPage, setPageSize, paged, total, totalPages,
 *           start, end } = usePaginated(items, { defaultPageSize: 10 });
 *
 * Re-pages automatically when `items` changes (e.g. filter applied).
 */
export function usePaginated(items, { defaultPageSize = 10 } = {}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Snap page back to 1 whenever the dataset shrinks below current window
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((items?.length || 0) / pageSize));
    if (page > totalPages) setPage(1);
  }, [items, page, pageSize]);

  return useMemo(() => {
    const total = items?.length || 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const start = (page - 1) * pageSize;
    const end = Math.min(start + pageSize, total);
    const paged = (items || []).slice(start, end);
    return { page, pageSize, setPage, setPageSize, paged, total, totalPages, start, end };
  }, [items, page, pageSize]);
}
